import { useTranslation } from "react-i18next";
import { Zap, LayoutGrid, GitBranch, HardDrive, Gauge, Key, Check, Sparkles } from "lucide-react";

interface PricingSectionProps {
  interval: "monthly" | "annual";
  onIntervalChange: (interval: "monthly" | "annual") => void;
  onCheckout: (plan: "pro" | "scale") => void;
  isPending: boolean;
}

/**
 * Pricing section for the free tier view.
 *
 * Shows interval toggle + two pricing cards (Pro / Scale)
 * with feature lists and upgrade CTAs.
 */
export function PricingSection({
  interval,
  onIntervalChange,
  onCheckout,
  isPending,
}: PricingSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Pricing Header & Interval Toggle */}
      <div className="text-center py-4">
        <h3 className="text-2xl font-extrabold text-heading tracking-tight">
          {t("billing.pricingTitle")}
        </h3>
        <p className="mt-2 text-sm text-muted max-w-md mx-auto">{t("billing.pricingSubtitle")}</p>

        {/* Interval Toggle */}
        <div className="mt-6 inline-flex items-center justify-center rounded-full bg-abyss p-1 border border-border">
          <button
            type="button"
            onClick={() => onIntervalChange("monthly")}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
              interval === "monthly"
                ? "bg-surface text-heading shadow-sm"
                : "text-muted hover:text-heading"
            }`}
          >
            {t("billing.intervalMonthly")}
          </button>
          <button
            type="button"
            onClick={() => onIntervalChange("annual")}
            className={`relative rounded-full px-4 py-1.5 text-xs font-semibold transition-all flex items-center gap-1.5 ${
              interval === "annual"
                ? "bg-surface text-heading shadow-sm"
                : "text-muted hover:text-heading"
            }`}
          >
            {t("billing.intervalAnnual")}
            <span className="inline-flex items-center rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success">
              {t("billing.savePercent")}
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards Grid */}
      <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto items-stretch">
        {/* Pro Plan Card */}
        <div className="relative rounded-2xl border border-border bg-surface p-8 flex flex-col justify-between hover:scale-[1.02] hover:border-indigo/40 transition-all duration-300 shadow-lg">
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-bold text-heading">{t("billing.proPlanName")}</h4>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo/10 text-indigo">
                <Zap size={16} />
              </span>
            </div>

            <div className="mt-4 flex items-baseline">
              <span className="text-4xl font-extrabold text-heading tracking-tight">
                {interval === "annual"
                  ? t("billing.yearlyPricingLabel")
                  : t("billing.monthlyPricingLabel")}
              </span>
              <span className="ml-1 text-sm text-muted">
                /
                {interval === "annual" ? t("billing.intervalAnnual") : t("billing.intervalMonthly")}
              </span>
            </div>
            <p className="text-xs text-muted mt-1.5">
              {interval === "annual" ? t("billing.billedAnnually") : t("billing.billedMonthly")}
            </p>

            {/* Features list */}
            <ul className="mt-6 space-y-4 border-t border-border/60 pt-6">
              <PricingFeature icon={LayoutGrid} text={t("billing.appsLimit", { count: 10 })} />
              <PricingFeature
                icon={GitBranch}
                text={t("billing.deploymentsLimit", { count: 20 })}
              />
              <PricingFeature
                icon={HardDrive}
                text={t("billing.storageLimit", { size: "50 GB" })}
              />
              <PricingFeature icon={Gauge} text={t("billing.apiRateLimitCount", { count: 500 })} />
              <PricingFeature icon={Key} text={t("billing.apiKeysLimit", { count: 10 })} />
            </ul>
          </div>

          <div className="mt-8">
            <button
              type="button"
              onClick={() => onCheckout("pro")}
              disabled={isPending}
              className="w-full inline-flex items-center justify-center rounded-xl bg-indigo py-3 px-4 font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 disabled:opacity-50"
            >
              {t("billing.upgradeBtn")}
            </button>
          </div>
        </div>

        {/* Scale Plan Card */}
        <div className="relative rounded-2xl border-2 border-indigo bg-elevated/40 p-8 flex flex-col justify-between hover:scale-[1.02] transition-all duration-300 shadow-xl overflow-visible">
          {/* Popular plan badge */}
          <div className="absolute top-2 right-6 bg-indigo text-white text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full shadow-md flex items-center gap-1 z-10">
            <Sparkles size={10} />
            {t("billing.popular")}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-bold text-heading">{t("billing.scalePlanName")}</h4>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan/10 text-cyan">
                <Sparkles size={16} />
              </span>
            </div>

            <div className="mt-4 flex items-baseline">
              <span className="text-4xl font-extrabold text-heading tracking-tight bg-gradient-to-br from-indigo-bright to-cyan bg-clip-text text-transparent">
                {interval === "annual"
                  ? t("billing.scaleYearlyPricingLabel")
                  : t("billing.scaleMonthlyPricingLabel")}
              </span>
              <span className="ml-1 text-sm text-muted">
                /
                {interval === "annual" ? t("billing.intervalAnnual") : t("billing.intervalMonthly")}
              </span>
            </div>
            <p className="text-xs text-muted mt-1.5">
              {interval === "annual" ? t("billing.billedAnnually") : t("billing.billedMonthly")}
            </p>

            {/* Features list */}
            <ul className="mt-6 space-y-4 border-t border-border/60 pt-6">
              <PricingFeature icon={LayoutGrid} text={t("billing.appsLimitUnlimited")} />
              <PricingFeature icon={GitBranch} text={t("billing.deploymentsLimitUnlimited")} />
              <PricingFeature
                icon={HardDrive}
                text={t("billing.storageLimit", { size: "500 GB" })}
              />
              <PricingFeature icon={Gauge} text={t("billing.apiRateLimitCount", { count: 2000 })} />
              <PricingFeature icon={Key} text={t("billing.apiKeysLimit", { count: 100 })} />
            </ul>
          </div>

          <div className="mt-8">
            <button
              type="button"
              onClick={() => onCheckout("scale")}
              disabled={isPending}
              className="w-full inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo to-cyan py-3 px-4 font-semibold text-white transition-all hover:opacity-95 hover:shadow-lg hover:shadow-cyan/20 disabled:opacity-50"
            >
              {t("billing.upgradeScaleBtn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PricingFeature (internal)
// ---------------------------------------------------------------------------

function PricingFeature({ icon: Icon, text }: { icon: typeof LayoutGrid; text: string }) {
  return (
    <li className="flex items-center gap-3 text-sm text-body">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
        <Check size={12} />
      </span>
      <Icon size={16} className="text-muted shrink-0" />
      <span className="font-medium">{text}</span>
    </li>
  );
}
